import Image from 'next/image';
import { useRouter } from 'next/router';
import { memo, useCallback, useMemo } from 'react';

import { useRouteChange } from '@/hooks/useRouteChange';
import { useTranslate } from '@/hooks/useTranslate';
import { AIFunction, ROUTE_FROM } from '@/utils/const';
import { EventNewID, PageVisitFrom } from '@/utils/eventlog/eventNewConfig';
import { trackEvent, usePageVisitTrack } from '@/utils/eventlog/logAdapter';
import {
  aiRemovalURL,
  aiVideoEditURL,
  faceSwapperURL,
  imageEnhanceURL,
  videoTranslateURL,
  voiceCloneURL,
} from '@/utils/routeConstants';

import styles from './entry.module.scss';

const AITools = memo(() => {
  const { t } = useTranslate();
  const { navigate } = useRouteChange();
  const router = useRouter();

  usePageVisitTrack(AIFunction.AITools);

  const tools = useMemo(
    () => [
      {
        id: 'ai-translate',
        title: t('fc.AITranslate'),
        image: '/images/3.0-ai-translate.png',
        href: videoTranslateURL,
        // toolName: AIFunction.VideoTranslate,
        toolName: 'ai_translate',
      },
      {
        id: 'ai-removal',
        title: t('bc.removal'),
        image: '/images/3.0-ai-removal.png',
        href: aiRemovalURL,
        toolName: AIFunction.Removal,
      },
      {
        id: 'face-swap',
        title: t('2-0'),
        image: '/images/3.0-faceSwap.png',
        href: faceSwapperURL,
        // toolName: AIFunction.FaceSwap,
        toolName: 'ai_swap',
      },
      {
        id: 'voice-clone',
        title: t('ce24a821ce9a84f071a4b291570d64b6'),
        image: '/images/3.0-voiceClone.png',
        href: voiceCloneURL,
        // toolName: AIFunction.VoiceClone,
        toolName: 'ai_clone',
      },
      {
        id: 'image-enhance',
        title: t('bc.ImageEnhance'),
        image: '/images/3.0-ai-enhance.png',
        href: imageEnhanceURL,
        toolName: AIFunction.ImageEnhance,
      },
      {
        id: 'video-editor',
        title: t('9613af8a82f375ffce54844fc0a470cc'),
        image: '/images/3.0-videoEdit.png',
        href: aiVideoEditURL,
        toolName: AIFunction.VideoEdit,
      },
      // {
      //   id: 'video-translation',
      //   title: t('merror.translation.title'),
      //   image: '/images/3.0-ai-removal.png',
      //   href: videoTranslateURL,
      // },
    ],
    [t],
  );

  const navigateTo = useCallback(
    (href: string, toolName: AIFunction | string) => {
      trackEvent(EventNewID.AI_TOOL_CLICK, {
        tool_name: toolName,
      });
      sessionStorage.setItem(ROUTE_FROM, PageVisitFrom.AI_TOOLS);
      navigate(() => {
        router.replace(href);
      });
    },
    [navigate, router],
  );

  return (
    <div className="flex justify-center overflow-y-auto no-scrollbar w-full h-full bg-app text-app-primary">
      <div className="flex flex-col gap-[16px] px-[32px] py-[16px] max-w-[1218px] w-full">
        {/* <span className="font-[500] text-[16px] leading-[22px]">
          {t('Popular Tools')}
        </span> */}
        <div className="flex flex-wrap gap-[16px] w-full">
          {tools.map(({ id, href, title, image, toolName }) => (
            <div
              key={id}
              className={styles.toolCard}
              style={{ width: 'calc(25% - 12px)' }}
              onClick={() => navigateTo(href, toolName)}
            >
              {/* Default gradient border, visible before hover. */}
              <div className={styles.toolCardDefaultBorder} />
              {/* Inner fill masks the gradient center and leaves only the border edge. */}
              <div className={styles.toolCardInner} />
              {/* Hover overlay draws the full blue border and inset glow. */}
              <div className={styles.toolCardHoverBorder} />
              {/* Actual card content sits above all decorative layers. */}
              <div className={styles.toolCardContent}>
                <span
                  className={`${styles.toolCardTitle} text-[14px] font-[500] leading-[20px]`}
                >
                  {title}
                </span>
                <div className="relative w-full h-[122px] rounded-[6px] overflow-hidden">
                  <Image src={image} alt={title} fill className="object-cover" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

AITools.displayName = 'AITools';
export default AITools;
